import _ from 'lodash';

import { ILevelGeneralData } from 'src/app/infrastructure/models/level-general-data';
import { autocomplete, AutoComplete, IAutoComplete } from '../../shared/controls/models/auto-complete.model';
import { IClaimForm } from './claim-form';
import { IClaimDto, claimDto } from 'src/assets/common/models/claim-dto.g';
import { IClaimRefDto } from 'src/assets/common/models/claim-ref-dto.g';
import { pageDto } from 'src/assets/common/models/page-dto.g';
import { pageComponentDto } from 'src/assets/common/models/page-component-dto.g';
import { claimActionDto } from 'src/assets/common/models/claim-action-dto.g';
import { activityData, ActivityData } from '../../activity/models/activity-data';

export interface IClaimCache {
    data: IClaimData;
    url: string;
}

export const claimData = {
    guid: 'guid',
    nr: 'nr',
    name: 'name',
    deleted: 'deleted',
    inactive: 'inactive',
    allowedReadOnly: 'allowedReadOnly',

    page: 'page',
    page_guid: 'page_guid',
    page_name: 'page_name',
    page_displayName: 'page_displayName',

    pageComponent: 'pageComponent',
    pageComponent_guid: 'pageComponent_guid',
    pageComponent_name: 'pageComponent_name',

    claimAction: 'claimAction',
    claimAction_guid: 'claimAction_guid',
    claimAction_name: 'claimAction_name',

    activitiesData: 'activitiesData'
} as const

export type TClaimData = (typeof claimData)[keyof typeof claimData];

export interface IClaimData extends ILevelGeneralData
{
    [claimData.guid]: string;
    [claimData.nr]: number | null;
    [claimData.name]: string;
    [claimData.deleted]: boolean;
    [claimData.inactive]: boolean;
    [claimData.allowedReadOnly]: boolean;

    [claimData.page]: IAutoComplete | null;
    [claimData.page_guid]: string | null;
    [claimData.page_name]: string | null;
    [claimData.page_displayName]: string | null;

    [claimData.pageComponent]: IAutoComplete | null;
    [claimData.pageComponent_guid]: string | null;
    [claimData.pageComponent_name]: string | null;

    [claimData.claimAction]: IAutoComplete | null;
    [claimData.claimAction_guid]: string | null;
    [claimData.claimAction_name]: string | null;

    [claimData.activitiesData]: ActivityData[];
}

export class ClaimData
    implements IClaimData, ILevelGeneralData
{
    [claimData.guid]!: string;
    [claimData.nr]: number | null = null;
    [claimData.name]!: string;
    [claimData.deleted]!: boolean;
    [claimData.inactive]!: boolean;
    [claimData.allowedReadOnly]!: boolean;

    [claimData.page]: IAutoComplete | null = null;
    [claimData.page_guid]: string | null = null;
    [claimData.page_name]: string | null = null;
    [claimData.page_displayName]: string | null = null;

    [claimData.pageComponent]: IAutoComplete | null = null;
    [claimData.pageComponent_guid]: string | null = null;
    [claimData.pageComponent_name]: string | null = null;

    [claimData.claimAction]: IAutoComplete | null = null;
    [claimData.claimAction_guid]: string | null = null;
    [claimData.claimAction_name]: string | null = null;

    [claimData.activitiesData]: ActivityData[] = [];

    protected constructor(modelDto?: IClaimDto | IClaimRefDto | null);
    protected constructor(modelData: IClaimData, modelForm: IClaimForm);
    protected constructor(value?: IClaimDto | IClaimRefDto | IClaimData | null, modelForm?: IClaimForm) {
        if (!!modelForm) {
            const _modelData = value as IClaimData;
            _.assign(this, _modelData, modelForm);
            this[claimData.page_guid] = modelForm[claimData.page]?.[autocomplete.itemId] as string ?? null;
            this[claimData.pageComponent_guid] = modelForm[claimData.pageComponent]?.[autocomplete.itemId] as string ?? null;
            this[claimData.claimAction_guid] = modelForm[claimData.claimAction]?.[autocomplete.itemId] as string ?? null;
        } else if (!!value) {
            const _modelDto = value as IClaimDto;

            this[claimData.guid] = _modelDto[claimDto.guid]!;
            this[claimData.nr] = _modelDto[claimDto.nr];
            this[claimData.name] = _modelDto[claimDto.name]!;
            this[claimData.deleted] = _modelDto[claimDto.deleted]!;
            this[claimData.inactive] = _modelDto[claimDto.inactive]!;
            this[claimData.allowedReadOnly] = _modelDto[claimDto.allowedReadOnly]!;

            this[claimData.page_guid] = _modelDto[claimDto.pageDto]?.[pageDto.guid] ?? null;
            this[claimData.page_name] = _modelDto[claimDto.pageDto]?.[pageDto.name] ?? null;
            this[claimData.page_displayName] = _modelDto[claimDto.pageDto]?.[pageDto.displayName] ?? null;
            this[claimData.page] = AutoComplete.create(this[claimData.page_guid]!, this[claimData.page_name]!);

            this[claimData.pageComponent_guid] = _modelDto[claimDto.pageComponentDto]?.[pageComponentDto.guid] ?? null;
            this[claimData.pageComponent_name] = _modelDto[claimDto.pageComponentDto]?.[pageComponentDto.name] ?? null;
            this[claimData.pageComponent] = AutoComplete.create(this[claimData.pageComponent_guid]!, this[claimData.pageComponent_name]!);

            this[claimData.claimAction_guid] = _modelDto[claimDto.claimActionDto]?.[claimActionDto.guid] ?? null;
            this[claimData.claimAction_name] = _modelDto[claimDto.claimActionDto]?.[claimActionDto.name] ?? null;
            this[claimData.claimAction] = AutoComplete.create(this[claimData.claimAction_guid]!, this[claimData.claimAction_name]!);

            this[claimData.activitiesData] = (_modelDto[claimDto.activitiesDto]
                ?.map(item => ActivityData.create(item))
                .filter((item): item is ActivityData => item !== null && item[activityData.deleted] === false)
                .sort((a, b) => (a[activityData.name] ?? '').localeCompare(b[activityData.name] ?? '')))
                ?? [];
        }
    }
        
    static create(modelDto?: IClaimDto | IClaimRefDto | null): IClaimData | null
    static create(modelData: IClaimData, modelForm: IClaimForm): IClaimData | null
    static create(value?: IClaimDto | IClaimRefDto | IClaimData | null, modelForm?: IClaimForm): IClaimData | null {

        if (!!modelForm) {
            const _modelData = value as IClaimData;
            return new ClaimData(_modelData, modelForm);
        } else {
            const _modelDto = value as IClaimDto;
            return _modelDto ? new ClaimData(_modelDto) : null;
        }
    }
}
